import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import dayjs from 'dayjs';
import { Spin, Typography, Table, Row, Col, Divider } from 'antd';
import './DirectorAdvanceReport.css'; // We will create this for print styles

const { Title, Text } = Typography;

const DirectorAdvanceReport = () => {
    const [searchParams] = useSearchParams();
    const directorId = searchParams.get('directorId');
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    const [data, setData] = useState([]);
    const [director, setDirector] = useState(null);
    const [company, setCompany] = useState({
        name: 'บริษัท อะตอม ซอฟท์ จำกัด', // Default or fetch from settings
        taxId: '0105561000000' // Default
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [directorId, startDate, endDate]);

    const fetchData = async () => {
        try {
            const [transRes, dirRes, companyRes] = await Promise.all([
                api.get(`/api/director-advance/transactions/${directorId}`, { params: { startDate, endDate } }),
                api.get('/api/director-advance/directors'),
                api.get('/api/settings/company') // Assuming this exists
            ]);

            if (transRes.data.success) {
                setData(transRes.data.data);
            }

            if (dirRes.data.success) {
                const d = dirRes.data.data.find(x => x.id == directorId);
                setDirector(d);
            }

            if (companyRes.data.success) {
                setCompany(companyRes.data.data);
            }

        } catch (error) {
            console.error('Error fetching report data', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', marginTop: 50 }}><Spin size="large" /></div>;

    const totalDebit = data.reduce((sum, item) => sum + item.debit, 0);
    const totalCredit = data.reduce((sum, item) => sum + item.credit, 0);
    const finalBalance = totalDebit - totalCredit;

    return (
        <div className="report-container">
            <div className="report-header">
                <Title level={3} style={{ textAlign: 'center', marginBottom: 0 }}>{company.name}</Title>
                <Text style={{ display: 'block', textAlign: 'center' }}>เลขทะเบียนนิติบุคคล: {company.taxId}</Text>
                <Title level={4} style={{ textAlign: 'center', marginTop: 10 }}>รายงานเงินทดรองจ่ายกรรมการ (Director Advance Report)</Title>

                <Row style={{ marginTop: 20 }}>
                    <Col span={12}>
                        <Text strong>ชื่อกรรมการ: </Text> <Text>{director?.name}</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Text strong>งวดบัญชี: </Text> <Text>{dayjs(startDate).format('DD/MM/YYYY')} - {dayjs(endDate).format('DD/MM/YYYY')}</Text>
                    </Col>
                </Row>
            </div>

            <table className="report-table">
                <thead>
                    <tr>
                        <th>วันที่</th>
                        <th>เลขที่เอกสาร</th>
                        <th>รายการ</th>
                        <th className="text-right">เดบิต (รับเงิน)</th>
                        <th className="text-right">เครดิต (เคลียร์)</th>
                        <th className="text-right">คงเหลือ</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => (
                        <tr key={index}>
                            <td>{dayjs(item.trans_date).format('DD/MM/YYYY')}</td>
                            <td>{item.ref_no}</td>
                            <td>{item.description}</td>
                            <td className="text-right">{item.debit > 0 ? item.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>
                            <td className="text-right">{item.credit > 0 ? item.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>
                            <td className="text-right">{item.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมทั้งสิ้น</td>
                        <td className="text-right font-bold">{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className="text-right font-bold">{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className="text-right font-bold">{finalBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    </tr>
                </tfoot>
            </table>

            <div className="report-summary" style={{ marginTop: 30 }}>
                <Row gutter={32}>
                    <Col span={12}>
                        <table className="summary-table">
                            <tbody>
                                <tr>
                                    <td>ยอดเงินทดรองรับทั้งหมด</td>
                                    <td className="text-right">{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr>
                                    <td>ยอดเคลียร์ค่าใช้จ่าย/คืน</td>
                                    <td className="text-right">{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr>
                                    <td><strong>ยอดเงินทดรองคงเหลือ ณ วันสิ้นงวด</strong></td>
                                    <td className="text-right"><strong>{finalBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </Col>
                </Row>
            </div>

            <div className="signatures" style={{ marginTop: 60 }}>
                <Row gutter={64}>
                    <Col span={12} style={{ textAlign: 'center' }}>
                        <div className="signature-line"></div>
                        <Text>({director?.name})</Text><br />
                        <Text>กรรมการผู้จัดการ</Text><br />
                        <Text>วันที่ ____/____/______</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: 'center' }}>
                        <div className="signature-line"></div>
                        <Text>(__________________________)</Text><br />
                        <Text>ผู้จัดทำบัญชี</Text><br />
                        <Text>วันที่ ____/____/______</Text>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default DirectorAdvanceReport;
